import React, { useCallback, useEffect, useState } from "react";
import { useAdminApi } from "./useAdminApi";

const PAGE_SIZE = 20;

// Read-only "what exactly is saved for this game" view: the games_igdb doc plus the
// raw IGDB cache doc for the same igdb_id, straight from GET /admin/igdb-games/:id.
// Rendered as JSON on purpose — this is the admin's inspection surface, not a
// designed detail page, and the raw IGDB payload has far too many fields to hand-lay
// out (and they change as IGDB does).
function IgdbGameModal({ apiFetch, entry, onClose }) {
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    function handleKeyDown(e) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  useEffect(() => {
    let cancelled = false;
    setDetail(null);
    setError(null);
    apiFetch(`/admin/igdb-games/${entry._id}`)
      .then((data) => { if (!cancelled) setDetail(data); })
      .catch((err) => { if (!cancelled && err.message !== "Forbidden") setError(err.message); });
    return () => { cancelled = true; };
  }, [apiFetch, entry._id]);

  return (
    <div className="admin-modal-overlay" onClick={onClose}>
      <div className="admin-modal admin-modal--wide admin-card glass" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="admin-modal-close" onClick={onClose} aria-label="Close">
          ×
        </button>
        <h3 className="admin-modal-title">{entry.name}</h3>
        <div className="admin-source-meta">
          {entry.platform_name} · <code className="admin-barcode-chip">{entry.igdb_id} &middot; {entry.platform_id}</code>
        </div>

        {!detail && !error && <p className="admin-status">Loading…</p>}
        {error && <p className="admin-status admin-error">{error}</p>}

        {detail && (
          <div className="admin-json-sections">
            <section>
              <h4 className="admin-json-heading">games_igdb</h4>
              <pre className="admin-json">{JSON.stringify(detail.igdb, null, 2)}</pre>
            </section>
            <section>
              <h4 className="admin-json-heading">
                IGDB raw cache
                {detail.raw?.updated_at && (
                  <span className="admin-source-created">fetched {new Date(detail.raw.updated_at).toLocaleString()}</span>
                )}
              </h4>
              {detail.raw
                ? <pre className="admin-json">{JSON.stringify(detail.raw, null, 2)}</pre>
                : <p className="admin-status">Never fetched live — no cached IGDB payload for igdb_id {entry.igdb_id}.</p>}
            </section>
          </div>
        )}
      </div>
    </div>
  );
}

function IgdbGamesTab({ user, onForbidden }) {
  const apiFetch = useAdminApi(user, onForbidden);
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [queryInput, setQueryInput] = useState("");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    const handle = setTimeout(() => {
      setQuery(queryInput.trim());
      setPage(1);
    }, 350);
    return () => clearTimeout(handle);
  }, [queryInput]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ page: String(page), page_size: String(PAGE_SIZE) });
      if (query) params.set("q", query);
      const data = await apiFetch(`/admin/igdb-games?${params.toString()}`);
      setItems(data.items);
      setTotalPages(data.total_pages || 1);
      setTotal(data.total || 0);
    } catch (err) {
      if (err.message !== "Forbidden") setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [apiFetch, page, query]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div>
      <div className="admin-links-search">
        <input
          type="text"
          placeholder="Search by name or igdb_id…"
          value={queryInput}
          onChange={(e) => setQueryInput(e.target.value)}
          className="admin-tgdb-link-input admin-links-search-input"
        />
      </div>

      {loading && <p className="admin-status">Loading…</p>}
      {error && <p className="admin-status admin-error">{error}</p>}

      <div className="admin-table glass">
        {items && items.length === 0 && <p className="admin-status">No games found.</p>}
        {items && items.map((item) => (
          <button type="button" className="admin-row admin-row--clickable" key={item._id} onClick={() => setSelected(item)}>
            {item.cover_url
              ? <img className="admin-boxart admin-boxart--row" src={item.cover_url} alt="" />
              : <div className="admin-boxart-placeholder admin-boxart-placeholder--row">No art</div>}
            <div className="admin-row-main">
              <div className="admin-source-name">{item.name}</div>
              <div className="admin-source-meta">{item.platform_name}</div>
              <div className="admin-source-meta">
                <code className="admin-barcode-chip">{item.igdb_id} &middot; {item.platform_id}</code>
                <span className="admin-source-created">{new Date(item.created_at).toLocaleString()}</span>
              </div>
            </div>
          </button>
        ))}
      </div>

      {items && items.length > 0 && (
        <div className="admin-pagination">
          <button
            type="button"
            className="admin-signout-btn"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1 || loading}
          >
            Previous
          </button>
          <span className="admin-pagination-status">
            Page {page} of {totalPages} · {total} total
          </span>
          <button
            type="button"
            className="admin-signout-btn"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages || loading}
          >
            Next
          </button>
        </div>
      )}

      {selected && <IgdbGameModal apiFetch={apiFetch} entry={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}

export default IgdbGamesTab;
